import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { faExclamationCircle, IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { StatelessPopup } from 'components/popup/stateless_popup/StatelessPopup';
import { useKeyPress } from 'custom_hooks/useKeyPress';
import { useOnClickOutside } from 'custom_hooks/useOnClickOutside';
import { ReactNode, useRef, useState } from 'react';
import styled from 'styled-components';
import { stopEvent } from 'utils/event';
import { pointerDownClickHandler } from 'utils/pointer_events';

const TooltipTrigger = styled.div`
    cursor: pointer;
`;

export const TooltipContainer = styled.div`
    padding: 1rem;
    height: 100%;
    width: 300px;
    color: var(--text-primary);
    font-family: REM;
    font-weight: 300;
    line-height: 1.8rem;
    background: var(--background-paper-elevation-8);
    border-radius: 0.3rem;
`;

type InfoPopupProps = {
    title: string;
    children: ReactNode;
    type?: 'info' | 'error';
    iconOverride?: IconDefinition;
};

export function InfoPopup({ title, children, type, iconOverride }: InfoPopupProps) {
    const [popupOpen, setPopupOpen] = useState(false);
    const buttonRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);

    const isErrorType = type === 'error';

    useKeyPress('Escape', () => setPopupOpen(false));

    const handlers = pointerDownClickHandler((e) => {
        e.preventDefault();
        e.stopPropagation();
        setPopupOpen((isOpen) => !isOpen);
    });

    useOnClickOutside([buttonRef, popupRef], () => {
        setPopupOpen(false);
    });

    return (
        <>
            <TooltipTrigger
                role="button"
                tabIndex={0}
                {...handlers}
                onClick={(e) => {
                    // clicking an info popup trigger should never ever
                    // propagate to anything behind it.
                    stopEvent(e);
                    handlers.onClick(e);
                }}
                aria-label={`${title} ${type?.charAt(0).toUpperCase() ?? 'Info'} Tooltip`}
                aria-expanded={popupOpen ? 'true' : 'false'}
                data-cy={`widget-wrapper-${title}-${type}-tooltip`}
                ref={buttonRef}
            >
                <FontAwesomeIcon
                    icon={
                        iconOverride ?? (isErrorType ? faExclamationCircle : faInfoCircle)
                    }
                    color={
                        isErrorType ? 'var(--error-default)' : 'var(--primary-default)'
                    }
                />
            </TooltipTrigger>
            {popupOpen && (
                <StatelessPopup
                    noPadding
                    noOverlay
                    anchor={buttonRef.current}
                    popupClassName="modal-popup"
                    onClose={() => setPopupOpen(false)}
                    ref={popupRef}
                >
                    <TooltipContainer>{children}</TooltipContainer>
                </StatelessPopup>
            )}
        </>
    );
}
